// Gentle hints for search syntax that does not do what it looks like (see the Vault's QueryParser):
// the text is indexed as words (letters and digits), "*" only works at the end of a word, ":" "=" "<" ">"
// start a field filter, a leading "-" excludes, OR must be written in capitals.

export interface SearchHintFix { label: string; query: string }
export interface SearchHint { id: string; message: string; fix?: SearchHintFix }

/** Splits on whitespace outside double quotes and parentheses, as the Vault does. */
export function splitTerms(q: string): string[] {
  const out: string[] = [];
  let sb = '';
  let quoted = false;
  let depth = 0;
  for (const c of q) {
    if (c === '"') quoted = !quoted;
    if (!quoted && c === '(' && (depth > 0 || sb === '' || sb === '-')) depth++;
    if (!quoted && c === ')' && depth > 0) depth--;
    if (/\s/.test(c) && !quoted && depth === 0) {
      if (sb) out.push(sb);
      sb = '';
    } else {
      sb += c;
    }
  }
  if (sb) out.push(sb);
  return out;
}

const replaceTerm = (q: string, term: string, next: string) => q.replace(term, next).trim();
const looksLikeField = (f: string) => /^@?[\p{L}_][\p{L}\p{N}_./-]*$/u.test(f) && f.length <= 40 && !f.includes('//');

export function searchHints(q: string): SearchHint[] {
  const hints: SearchHint[] = [];
  if (!q.trim()) return hints;
  if ((q.match(/"/g)?.length ?? 0) % 2 === 1) {
    hints.push({ id: 'quote', message: 'A closing quote is missing, so the rest of the search is read as one phrase.', fix: { label: 'Add the quote', query: `${q}"` } });
  }
  const terms = splitTerms(q);
  for (const t of terms) {
    if (t.startsWith('"') || t.startsWith('@') || t.startsWith('(') || t === 'OR') continue;
    const opAt = [...t].findIndex((c) => ':=<>'.includes(c));
    if (opAt > 0 && (!looksLikeField(t.slice(0, opAt)) || t.slice(opAt + 1).startsWith('//')) && !t.startsWith('-')) {
      hints.push({
        id: 'filter', message: `“${t}” is read as a field filter (field “${t.slice(0, opAt)}”). To search for that text, put it in quotes.`,
        fix: { label: `Search "${t}"`, query: replaceTerm(q, t, `"${t}"`) },
      });
      continue;
    }
    if (opAt > 0) continue;
    if (/^-\d/.test(t)) {
      hints.push({ id: 'minus', message: `A leading - excludes: “${t}” leaves out documents with ${t.slice(1)}. To search for the text itself, put it in quotes.`, fix: { label: `Search "${t}"`, query: replaceTerm(q, t, `"${t}"`) } });
      continue;
    }
    const core = t.replace(/^-/, '');
    if (/^\*+[\p{L}\p{N}]/u.test(core)) {
      const word = core.replace(/^\*+/, '').replace(/\*+$/, '');
      hints.push({
        id: 'leading-star',
        message: `A star at the start is ignored: “${t}” finds the whole word “${word}”, not longer words that only end in it (“…${word}”). Searches match whole words or word beginnings.`,
        fix: { label: `Search ${word}*`, query: replaceTerm(q, t, `${t.startsWith('-') ? '-' : ''}${word}*`) },
      });
    } else if (/[\p{L}\p{N}]\*[\p{L}\p{N}]/u.test(core)) {
      const head = core.slice(0, core.indexOf('*'));
      hints.push({
        id: 'inner-star', message: `A star inside a word is ignored; use it only at the end: “${head}*” finds words that begin with “${head}”.`,
        fix: { label: `Search ${head}*`, query: replaceTerm(q, t, `${t.startsWith('-') ? '-' : ''}${head}*`) },
      });
    }
  }
  if (terms.some((t, i) => t === 'or' && i > 0 && i < terms.length - 1)) {
    hints.push({ id: 'or', message: 'Write OR in capitals to mean “either one”; a lowercase “or” is searched as a word.', fix: { label: 'Use OR', query: terms.map((t, i) => (t === 'or' && i > 0 && i < terms.length - 1 ? 'OR' : t)).join(' ') } });
  }
  return hints.slice(0, 2);
}
